import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import sum from 'lodash/sum';

import {renameEvent} from 'common/report/util/events';
import {removeByIndex, replaceByIndex} from 'common/util/array';
import {CustomEventPropertyFilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {FiltersOperator} from 'common/widgets/query-entry-section/types';

import type {Alternative, LabelPart, MPEvent, SerializedCustomEvent} from './types';

export class EventSelector {
  public static fromAlternative({event, valid_segfilter: validSegFilter}: Alternative): EventSelector {
    let options: Partial<EventSelector> = {event};
    if (validSegFilter) {
      const {filters, op} = validSegFilter;
      options = {
        ...options,
        propertyFilters: filters.map((filter) => CustomEventPropertyFilter.fromSegfilter(filter)),
        filtersOperator: op as FiltersOperator,
      };
    }

    return new EventSelector(options);
  }

  public event: MPEvent;
  public propertyFilters: CustomEventPropertyFilter[];
  public filtersOperator: FiltersOperator;

  constructor(options: Partial<EventSelector> = {}) {
    const {event, propertyFilters = [], filtersOperator = FiltersOperator.And} = options;
    // event is either string for normal event or object representing custom event
    this.event = isObject(event) ? new MPCustomEvent(event as Partial<MPCustomEvent>) : event;
    this.propertyFilters = propertyFilters.map((pf) => new CustomEventPropertyFilter(pf));
    this.propertyFilters = propertyFilters.map((pf) => new CustomEventPropertyFilter(pf));
    this.filtersOperator = filtersOperator;
  }

  public toAlternative(): Alternative {
    return {
      event: this.isCustomEvent(this.event) ? this.event.getSerializedName() : this.event,
      valid_segfilter: {
        filters: this.propertyFilters.map((pf) => pf.toSegfilter()),
        op: this.filtersOperator,
      },
    };
  }

  public getLabelParts(): LabelPart[] {
    const operatorLabel = this.filtersOperator.toUpperCase();
    const propertyFilterParts = this.propertyFilters.reduce(
      (parts, propertyFilter, index, arr) => {
        const operator = index !== arr.length - 1 ? [{label: `\n${operatorLabel}`, highlight: false}] : [];
        return [...parts, ...propertyFilter.getLabelParts(), ...operator];
      },
      [{label: `where`, highlight: false}],
    );

    return [
      {label: this.getEventLabel(), highlight: true},
      ...(this.propertyFilters.length ? propertyFilterParts : []),
    ];
  }

  public isValid(): boolean {
    return !!this.event && this.propertyFilters.every((pf) => pf.isValid());
  }

  public hasSelectedEvent(): boolean {
    return !!this.event;
  }

  public getEventLabel(): string {
    const name = this.isCustomEvent(this.event) ? this.event.name : this.event;
    return renameEvent(name);
  }

  public getEventName(): string {
    return this.isCustomEvent(this.event) ? this.event.getSerializedName() : this.event;
  }

  public getEventId(): number {
    return this.isCustomEvent(this.event) ? this.event.id : null;
  }

  public cloneEventSelector(newAttributes: Partial<EventSelector>): EventSelector {
    return new EventSelector({
      event: this.event,
      propertyFilters: this.propertyFilters,
      ...newAttributes,
    });
  }

  public updateEvent(event: MPEvent): EventSelector {
    return new EventSelector({event});
  }

  public commitPropertyFilter(propertyFilter: CustomEventPropertyFilter, index: number): EventSelector {
    let propertyFilters;
    if (index >= this.propertyFilters.length) {
      propertyFilters = [...this.propertyFilters, propertyFilter];
    } else {
      propertyFilters = replaceByIndex(this.propertyFilters, index, propertyFilter);
    }

    return this.cloneEventSelector({propertyFilters});
  }

  public removePropertyFilterAtIndex(index: number): EventSelector {
    return this.cloneEventSelector({
      propertyFilters: removeByIndex(this.propertyFilters, index),
    });
  }

  public eventIsCustom(): boolean {
    return this.isCustomEvent(this.event);
  }

  private isCustomEvent(event: MPEvent): event is MPCustomEvent {
    return event instanceof MPCustomEvent;
  }
}

export class MPCustomEvent {
  public static fromSerializedCustomEvent({alternatives, name, id}: SerializedCustomEvent): MPCustomEvent {
    const eventSelectors = alternatives.map((alternative) => EventSelector.fromAlternative(alternative));
    return new MPCustomEvent({id, name, eventSelectors});
  }

  public id?: number;
  public name: string;
  public eventSelectors: EventSelector[];
  public custom = true;

  constructor(options: Partial<MPCustomEvent> = {}) {
    const {id, name = ``, eventSelectors = null} = options;
    if (id) {
      this.id = id;
    }

    this.name = name;
    this.eventSelectors = eventSelectors ? eventSelectors.map((s) => new EventSelector(s)) : [];
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  static get CUSTOM_EVENT_PREFIX(): string {
    return `$custom_event:`;
  }

  public getSerializedName(): string {
    return `${MPCustomEvent.CUSTOM_EVENT_PREFIX}${this.id}`;
  }

  public getLabelParts(): LabelPart[] {
    return this.eventSelectors.reduce((parts, selector, index, arr) => {
      const operator = index !== arr.length - 1 ? [{label: `\nOR`, highlight: false}] : [];
      return [...parts, ...selector.getLabelParts(), ...operator];
    }, []);
  }

  public toSerializedCustomEvent(): SerializedCustomEvent {
    const alternatives = this.eventSelectors
      .filter((eventSelector) => eventSelector.isValid())
      .map((eventSelector) => eventSelector.toAlternative());
    return {
      ...pick(this, [`id`]),
      alternatives,
      name: this.name.trim(),
    };
  }

  public isValid(): boolean {
    return (
      this.eventSelectors.some((eventSelector) => eventSelector.isValid()) &&
      this.eventSelectors.every((eventSelector, idx, arr) => eventSelector.isValid() || idx === arr.length - 1)
    );
  }

  public updateName(name: string): MPCustomEvent {
    return new MPCustomEvent({...this.getAttrs(), name});
  }

  public toTrackingData(): Obj {
    return {
      '# alternatives': this.eventSelectors.length,
      '# property filters': sum(this.eventSelectors.map((selector) => selector.propertyFilters.length)),
      'custom event id': this.id,
      name: this.name,
    };
  }

  public getAttrs(): Partial<MPCustomEvent> {
    return pick(this, [`eventSelectors`, `id`, `name`]);
  }
}
